import React, { useEffect, useState } from "react";
import SalonService from "../../services/SalonService";
import LoadingIndicator from "../loadingIndicator/LoadingIndicator";
import "./MembershipPlans.css";
import { useSalon } from "../../context/SalonContext";
import { FaSpinner, FaCrown, FaCheckCircle, FaGift, FaPercentage, FaChevronRight, FaRegCalendarAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import "../../style.css";
import NoSalonSelected from "../salon/NoSalonSelected";

const MembershipPlans = () => {
    const { accessToken, salonUuid, customerId, mobileNumber, salonName } = useSalon();
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [loadingPlanId, setLoadingPlanId] = useState(null);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                let mySalonUuid = salonUuid ? salonUuid : localStorage.getItem("SALON_UUID");
                if (mySalonUuid) {
                    const response = await SalonService.getSalonMembershipPlans(mySalonUuid);
                    setPlans(response.data?.membershipPlans || []);
                }
            } catch (err) {
                console.error("Error fetching membership plans:", err);
                toast.error("Failed to load membership plans");
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, [accessToken, salonUuid]);

    const handlePurchase = async (planId) => {
        try {
            if (!salonUuid) {
                toast.error("Salon details not found.");
                return;
            }

            setLoadingPlanId(planId);

            const data = { planId, customerId, mobileNumber }
            const response = await SalonService.purchaseMembership(
                salonUuid,
                data
            );

            if (response.status === 200) {
                toast.success("🎉 Membership purchased successfully!");
            }
        } catch (error) {
            console.error("Purchase failed:", error);
            toast.error(
                error.response?.data?.message || "❌ Failed to purchase membership. Try again."
            );
        } finally {
            setLoadingPlanId(null);
        }
    };

    if (!salonUuid && !localStorage.getItem("SALON_UUID")) {
        return <NoSalonSelected />;
    }

    return (
        <div className="membership-page-wrapper">
            <div className="membership-content-container py-4">
                {loading && <LoadingIndicator />}

                {!loading && plans.length === 0 && (
                    <div className="empty-state-memberships">
                        <div className="empty-icon">💎</div>
                        <h3>No Plans Available</h3>
                        <p>Stay tuned! We'll be launching exciting membership plans soon.</p>
                    </div>
                )}

                <div className="plans-grid-premium">
                    {plans.map((plan, index) => {
                        const isExpiryPlan = plan.validityType?.toLowerCase() === "expirydate" && plan.expiryDate;
                        let isExpired = false;
                        let isExpiringSoon = false;
                        let validityText = "";

                        if (isExpiryPlan) {
                            const expiryDate = new Date(plan.expiryDate);
                            const today = new Date();
                            const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

                            if (diffDays < 0) {
                                isExpired = true;
                                validityText = `Expired: ${expiryDate.toLocaleDateString()}`;
                            } else if (diffDays <= 7) {
                                isExpiringSoon = true;
                                validityText = `Expires: ${expiryDate.toLocaleDateString()}`;
                            } else {
                                validityText = `Valid until: ${expiryDate.toLocaleDateString()}`;
                            }
                        } else {
                            validityText = `Validity: ${plan.durationInDays} Days`;
                        }

                        return (
                            <div
                                key={plan.id}
                                className={`premium-plan-card ${isExpired ? "expired-plan-card" : ""}`}
                            >
                                {isExpired && <div className="premium-badge-expired">Expired</div>}
                                {index === 0 && !isExpired && (
                                    <div className="premium-badge-recommended">
                                        <FaCrown className="me-1" /> Recommended
                                    </div>
                                )}

                                <div className="plan-header-premium">
                                    <div className="plan-icon-wrapper">
                                        <FaCrown className="crown-icon" />
                                    </div>
                                    <h3 className="plan-name-text">{plan.name}</h3>
                                    <p className="plan-desc-text">{plan.description}</p>
                                </div>

                                <div className="plan-pricing-section">
                                    <div className="price-box">
                                        <span className="currency">₹</span>
                                        <span className="amount">{plan.price}</span>
                                    </div>
                                    <div className={`validity-tag ${isExpiringSoon ? 'warning' : isExpired ? 'danger' : ''}`}>
                                        <FaRegCalendarAlt className="me-1" /> {validityText}
                                    </div>
                                </div>

                                <div className="premium-plan-benefits">
                                    <h4 className="benefits-title">Exclusive Benefits</h4>
                                    <ul className="benefits-list-modern">
                                        {plan.benefits.map((benefit, idx) => {
                                            let icon = <FaCheckCircle className="text-success" />;
                                            if (/free/i.test(benefit)) icon = <FaGift className="text-warning" />;
                                            else if (/discount/i.test(benefit)) icon = <FaPercentage className="text-primary" />;

                                            return (
                                                <li key={idx} className="benefit-item-modern">
                                                    <span className="benefit-icon-modern">{icon}</span>
                                                    <span className="benefit-text-modern">{benefit}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>

                                <div className="plan-footer-premium">
                                    {!isExpired && (
                                        <button
                                            className="buy-now-btn-premium"
                                            onClick={() => handlePurchase(plan.id)}
                                            disabled={loadingPlanId === plan.id}
                                        >
                                            {loadingPlanId === plan.id ? (
                                                <>
                                                    <FaSpinner className="icon-spin" /> Processing…
                                                </>
                                            ) : (
                                                <>
                                                    Buy Membership <FaChevronRight className="ms-2" />
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MembershipPlans;
